import { FCElement } from '@fc/component';
import { VariantPickEvent, VariantChangeEvent } from '@fc/events';
import { patchDOM } from '@fc/dom-patch';
import { sectionManager } from '@fc/sections';
import { createAbortError, isAbortError, LatestRequestController } from '@fc/section-response';
import { requestYieldCallback } from '@fc/utilities';
import { ProductSelectEvent } from '@theme/standard-events';

export const CARD_RENDER_SECTION_ID = 'section-rendering-product-card';
const CARD_RENDER_SECTION_IDS = new Set([CARD_RENDER_SECTION_ID]);

const MARKET_CONTEXT_QUERY_KEYS = ['country', 'locale'];

function preserveMarketContext(url) {
  const current = new URL(window.location.href);

  for (const key of MARKET_CONTEXT_QUERY_KEYS) {
    const value = current.searchParams.get(key);
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  }

  return url;
}

function usesCardRenderSection(fetchUrl) {
  try {
    const url = new URL(fetchUrl, window.location.origin);
    return CARD_RENDER_SECTION_IDS.has(url.searchParams.get('section_id'));
  } catch {
    return Array.from(CARD_RENDER_SECTION_IDS).some((sectionId) => fetchUrl.includes(`section_id=${sectionId}`));
  }
}

function createProductSelectDeferred() {
  if (typeof ProductSelectEvent?.createPromise === 'function') {
    return ProductSelectEvent.createPromise();
  }

  let resolve;
  let reject;
  const promise = new Promise((resolvePromise, rejectPromise) => {
    resolve = resolvePromise;
    reject = rejectPromise;
  });

  return { promise, resolve, reject };
}

function compactObject(value) {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry != null && entry !== ''));
}

function handleFromURL(value) {
  if (typeof value !== 'string' || value.trim() === '') return null;

  try {
    const url = new URL(value, window.location.origin);
    const segments = url.pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] || null;
  } catch {
    const segments = value.split('?')[0].split('/').filter(Boolean);
    return segments[segments.length - 1] || null;
  }
}

function moneyFromVariantPrice(value) {
  if (value == null || value === '') return null;

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;

  const amount = Number.isInteger(numeric) && numeric >= 100 ? numeric / 100 : numeric;
  const currencyCode = window.FitCheck?.money?.currency_code || window.Shopify?.currency?.active || null;
  if (!currencyCode) return null;

  return {
    amount: amount.toFixed(2),
    currencyCode,
  };
}

function normalizeVariantForProductSelect(resource, selectedOptions = []) {
  if (!resource || typeof resource !== 'object') return null;

  return compactObject({
    id: resource.id,
    title: resource.title ?? resource.public_title,
    availableForSale: resource.availableForSale ?? resource.available_for_sale ?? resource.available,
    price: moneyFromVariantPrice(resource.price),
    selectedOptions,
  });
}

// Manages product variant selection, section fetching, URL sync, and combined listings
export default class VariantPicker extends FCElement {
  #pendingPath;
  #requestController = new LatestRequestController();

  connectedCallback() {
    super.connectedCallback();
    this.addManagedEventListener(this, 'change', this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#requestController.abort();
  }

  #onChange = (evt) => {
    this.variantChanged(evt);
  };

  variantChanged(evt) {
    const target = evt?.target;
    if (!(target instanceof HTMLElement)) return;

    // Resolve the active option element
    const chosenOption = this.resolveChosenOption(target);
    if (!chosenOption) return;

    this.updateSelectedOption(target);

    const optionValueIds = this.getOptionValueIds(chosenOption);
    const currentPath = this.dataset.productUrl?.split('?')[0];
    const linkedPath = chosenOption.dataset.productUrl || chosenOption.dataset.connectedProductUrl;
    const productSelect = this.#dispatchProductSelectEvent(chosenOption);

    this.dispatchEvent(
      new VariantPickEvent({
        id: chosenOption.dataset.optionValueId ?? '',
        mediaId: chosenOption.dataset.optionMediaId ?? '',
        optionValueIds,
        productId: this.dataset.productId ?? '',
        productUrl: (linkedPath || currentPath || '').split('?')[0],
        selectedOptions: productSelect.selectedOptions,
        standardEventDispatched: true,
        variantId: chosenOption.dataset.variantId ?? '',
      }),
    );

    const isMainCtx =
      this.dataset.templateProductMatch === 'true' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    const loadNewProduct = isMainCtx && !!linkedPath && linkedPath !== currentPath;

    const fetchUrl = this.buildRequestUrl(chosenOption);
    this.fetchUpdatedSection(fetchUrl, loadNewProduct, productSelect);

    if (isMainCtx) {
      const historyUrl = this.buildHistoryUrl(
        linkedPath || currentPath || window.location.pathname,
        optionValueIds,
        chosenOption.dataset.variantId ?? '',
      );
      if (historyUrl && historyUrl.href !== window.location.href) {
        requestYieldCallback(() => history.replaceState({}, '', historyUrl.toString()));
      }
    }
  }

  updateSelectedOption(targetElement) {
    // Accept an option-value-id string and resolve to an element
    if (typeof targetElement === 'string') {
      targetElement = this.querySelector(`[data-option-value-id="${targetElement}"]`);
      if (!targetElement) return;
    }
    if (!(targetElement instanceof Element)) return;

    if (targetElement instanceof HTMLOptionElement) {
      const ownerSelect = targetElement.parentElement;
      if (!(ownerSelect instanceof HTMLSelectElement)) return;
      ownerSelect.value = targetElement.value;
      for (const option of ownerSelect.options) option.removeAttribute('selected');
      targetElement.setAttribute('selected', 'selected');
      this.#syncLinkedOptionControls(targetElement);
      return;
    }

    // Radio input
    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#syncLinkedOptionControls(targetElement);
      return;
    }

    // Select dropdown — sync the selected attribute to match current value
    if (targetElement instanceof HTMLSelectElement) {
      const match = Array.from(targetElement.options).find((o) => o.value === targetElement.value);
      if (!match) return;
      for (const o of targetElement.options) o.removeAttribute('selected');
      match.setAttribute('selected', 'selected');
      this.#syncLinkedOptionControls(match);
    }
  }

  resolveChosenOption(targetElement) {
    if (targetElement instanceof HTMLSelectElement) {
      return targetElement.options[targetElement.selectedIndex] ?? null;
    }

    if (targetElement instanceof HTMLOptionElement || targetElement instanceof HTMLInputElement) {
      return targetElement;
    }

    return null;
  }

  buildRequestUrl(chosenOption, sourceContext = null, sourceOptionValues = []) {
    // Resolve product path (combined-listing aware)
    const path =
      chosenOption.dataset.productUrl ||
      chosenOption.dataset.connectedProductUrl ||
      this.#pendingPath ||
      this.dataset.productUrl;
    if (!path) throw new Error('Product path is required');
    this.#pendingPath = path;

    const params = [];
    const optionValueIds =
      Array.isArray(sourceOptionValues) && sourceOptionValues.length
        ? sourceOptionValues.filter(Boolean)
        : this.getOptionValueIds(chosenOption);

    // Collect option_values depending on context
    if (optionValueIds.length && (!sourceContext || sourceContext === 'product-card')) {
      params.push(`option_values=${optionValueIds.join(',')}`);
    }

    // Quick-add / swatches context uses a fixed section id
    if (this.closest('quick-add') || this.closest('swatch-picker')) {
      const url = preserveMarketContext(new URL(path, window.location.origin));

      url.searchParams.delete('variant');
      url.searchParams.set('section_id', CARD_RENDER_SECTION_ID);
      for (const param of params) {
        const [key, value] = param.split('=');
        if (key && value) url.searchParams.set(key, value);
      }
      return `${url.pathname}${url.search}`;
    }

    const url = preserveMarketContext(new URL(path, window.location.origin));
    url.searchParams.delete('variant');
    for (const param of params) {
      const [key, value] = param.split('=');
      if (key && value) url.searchParams.set(key, value);
    }
    return `${url.pathname}${url.search}`;
  }

  buildHistoryUrl(productPath, optionValueIds = [], variantId = '') {
    if (!productPath) return null;

    const url = new URL(window.location.href);
    url.pathname = productPath.split('?')[0];

    if (variantId) {
      url.searchParams.set('variant', variantId);
      url.searchParams.delete('option_values');
    } else if (optionValueIds.length) {
      url.searchParams.set('option_values', optionValueIds.join(','));
      url.searchParams.delete('variant');
    } else {
      url.searchParams.delete('option_values');
      url.searchParams.delete('variant');
    }

    return url;
  }

  buildLinkedProductUrl(chosenOption = null, optionValueIds = []) {
    const option = this.resolveChosenOption(chosenOption ?? this.selectedOption);
    const currentPath = this.dataset.productUrl?.split('?')[0];
    const productPath = option?.dataset.productUrl || option?.dataset.connectedProductUrl || currentPath;
    const ids = Array.isArray(optionValueIds) && optionValueIds.length ? optionValueIds : this.getOptionValueIds(option);

    return this.buildHistoryUrl(productPath, ids, option?.dataset.variantId ?? '');
  }

  prefetchUpdatedSection(fetchUrl) {
    if (!fetchUrl || window.Shopify?.designMode === true) return Promise.resolve(null);

    return sectionManager.getHTMLDocument(fetchUrl, {
      cache: true,
    }).catch(() => null);
  }

  async fetchUpdatedSection(fetchUrl, shouldMorphEntirePage = false, productSelect = null) {
    if (!fetchUrl) {
      this.#rejectProductSelect(productSelect, new Error('Variant request URL is required'));
      return;
    }

    const request = this.#requestController.start();

    try {
      const doc = await this.#getFreshDocument(fetchUrl, request.signal, shouldMorphEntirePage);

      if (!request.isCurrent()) {
        this.#rejectProductSelect(productSelect, createAbortError());
        return;
      }

      // Allow overflow-list to render immediately
      doc.querySelector('overflow-row[defer]')?.removeAttribute('defer');

      const freshPicker = this.#findFreshPicker(doc);
      const variantJson =
        freshPicker?.querySelector('script[type="application/json"]')?.textContent ??
        doc.querySelector('variant-select script[type="application/json"], swatch-picker script[type="application/json"]')
          ?.textContent;
      if (!variantJson) {
        this.#resolveProductSelect(productSelect, null, {
          html: doc,
          productId: freshPicker?.dataset?.productId || this.dataset.productId || '',
          sourceId: this.selectedOptionId ?? '',
          resource: null,
        });
        return;
      }

      this.#pendingPath = undefined;
      const variantResource = JSON.parse(variantJson);
      let newProduct;

      if (shouldMorphEntirePage) {
        newProduct = this.#productChangeFromFreshPicker(freshPicker);
        this.updateMain(doc);
      }

      if (!shouldMorphEntirePage) {
        newProduct = this.updateVariantPicker(doc);
      }

      const optId = this.selectedOptionId;
      const detail = {
        html: doc,
        productId: freshPicker?.dataset?.productId || this.dataset.productId || '',
        newProduct,
        sourceId: optId ?? '',
        resource: variantResource,
      };

      if (!shouldMorphEntirePage && optId) {
        this.dispatchEvent(
          new VariantChangeEvent(variantResource, optId, {
            html: doc,
            productId: this.dataset.productId ?? '',
            newProduct,
          }),
        );
      }

      this.#resolveProductSelect(productSelect, variantResource, detail);
    } catch (error) {
      this.#rejectProductSelect(productSelect, error);

      if (!isAbortError(error)) {
        return;
      }

      return;
    } finally {
      request.finish();
    }
  }

  #getFreshDocument(fetchUrl, signal, shouldMorphEntirePage) {
    if (shouldMorphEntirePage) {
      return sectionManager.getHTMLDocument(fetchUrl, {
        cache: false,
        signal,
      });
    }

    if (usesCardRenderSection(fetchUrl)) {
      return sectionManager.getHTMLDocument(fetchUrl, {
        cache: window.Shopify?.designMode !== true,
        signal,
      });
    }

    if (this.dataset.sectionId) {
      return sectionManager.getSectionDocument(this.dataset.sectionId, {
        cache: false,
        signal,
        url: fetchUrl,
      });
    }

    return sectionManager.getHTMLDocument(fetchUrl, {
      cache: false,
      signal,
    });
  }

  updateVariantPicker(freshDoc) {
    const freshEl = this.#findFreshPicker(freshDoc);
    if (!freshEl) throw new Error('Fresh picker element not found');

    const productChange = this.#productChangeFromFreshPicker(freshEl);
    if (freshEl instanceof HTMLElement) {
      const { productId, productUrl } = freshEl.dataset;
      if (productId) this.dataset.productId = productId;
      if (productUrl) this.dataset.productUrl = productUrl;
    }

    patchDOM(this, freshEl);

    return productChange;
  }

  updateMain(freshDoc) {
    const currentMain = document.querySelector('main');
    const freshMain = freshDoc.querySelector('main');
    if (!(currentMain instanceof HTMLElement) || !(freshMain instanceof HTMLElement)) {
      throw new Error('Main elements not found');
    }
    patchDOM(currentMain, freshMain);
  }

  get selectedOption() {
    const el = this.querySelector('select option[selected], fieldset input:checked');
    if (el instanceof HTMLInputElement || el instanceof HTMLOptionElement) return el;
    return undefined;
  }

  get selectedOptionId() {
    return this.selectedOption?.dataset.optionValueId ?? undefined;
  }

  get selectedOptionsValues() {
    return this.getOptionValueIds();
  }

  getAllSelectedOptions(targetElement = null) {
    const selectedByPosition = new Map();

    Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).forEach((el) => {
      this.#setSelectedOption(selectedByPosition, el);
    });

    const overrideElement = this.resolveChosenOption(targetElement);
    if (overrideElement) {
      this.#setSelectedOption(selectedByPosition, overrideElement);
    }

    return Array.from(selectedByPosition.entries())
      .sort(([positionA], [positionB]) => {
        const numericA = Number(positionA);
        const numericB = Number(positionB);
        if (Number.isFinite(numericA) && Number.isFinite(numericB)) return numericA - numericB;
        return String(positionA).localeCompare(String(positionB));
      })
      .map(([, option]) => option);
  }

  #findFreshPicker(sourceRoot) {
    const tag = this.tagName.toLowerCase();
    const blockId = this.dataset.blockId;

    const candidates = Array.from(sourceRoot.querySelectorAll(tag));
    if (blockId) {
      const blockMatch = candidates.find(
        (candidate) => candidate instanceof HTMLElement && candidate.dataset.blockId === blockId,
      );
      if (blockMatch) return blockMatch;
    }

    return sourceRoot.querySelector(tag);
  }

  #dispatchProductSelectEvent(chosenOption) {
    const deferred = createProductSelectDeferred();
    const selectedOptions = this.getAllSelectedOptions(chosenOption);

    this.dispatchEvent(
      new ProductSelectEvent({
        product: this.#productPayload(chosenOption),
        selectedOptions,
        detail: {
          optionValueId: chosenOption.dataset.optionValueId ?? '',
        },
        promise: deferred.promise,
      }),
    );

    return { ...deferred, selectedOptions };
  }

  #productPayload(chosenOption = null) {
    const productUrl =
      chosenOption?.dataset?.productUrl ||
      chosenOption?.dataset?.connectedProductUrl ||
      this.dataset.productUrl ||
      window.location.pathname;

    return compactObject({
      id: this.dataset.productId ?? '',
      title:
        this.dataset.productTitle ||
        this.querySelector('[data-product-title]')?.textContent?.trim() ||
        document.querySelector('h1')?.textContent?.trim() ||
        '',
      handle: this.dataset.productHandle || handleFromURL(productUrl),
    });
  }

  #resolveProductSelect(productSelect, variantResource, detail = {}) {
    if (!productSelect?.resolve) return;

    productSelect.resolve({
      variant: normalizeVariantForProductSelect(variantResource, productSelect.selectedOptions || []),
      detail,
    });
  }

  #rejectProductSelect(productSelect, error) {
    if (!productSelect?.reject) return;
    productSelect.reject(error);
  }

  #productChangeFromFreshPicker(freshEl) {
    if (!(freshEl instanceof HTMLElement)) return undefined;

    const { productId, productUrl } = freshEl.dataset;
    if (productId && productUrl && this.dataset.productId !== productId) {
      return { id: productId, url: productUrl };
    }

    return undefined;
  }

  getOptionValueIds(targetElement = null) {
    const valuesByPosition = new Map();

    Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).forEach((el) => {
      this.#setOptionValue(valuesByPosition, el);
    });

    const overrideElement = this.resolveChosenOption(targetElement);
    if (overrideElement) {
      this.#setOptionValue(valuesByPosition, overrideElement);
    }

    return Array.from(valuesByPosition.values()).filter(Boolean);
  }

  #setSelectedOption(selectedByPosition, element) {
    if (!(element instanceof HTMLElement)) return;

    const name = this.#resolveOptionName(element);
    const value = this.#resolveOptionValueName(element);
    if (!name || !value) return;

    selectedByPosition.set(this.#resolveOptionPosition(element), { name, value });
  }

  #setOptionValue(valuesByPosition, element) {
    const optionValueId = element?.dataset?.optionValueId;
    if (!optionValueId) return;

    const optionPosition = this.#resolveOptionPosition(element);
    valuesByPosition.set(optionPosition, optionValueId);
  }

  #resolveOptionName(element) {
    if (element?.dataset?.optionName) return element.dataset.optionName;

    if (element instanceof HTMLOptionElement) {
      const select = element.parentElement;
      return select?.getAttribute('aria-label') || select?.name?.replace(/^options\[(.*)\]$/, '$1') || '';
    }

    return element.closest('fieldset')?.querySelector('legend')?.textContent?.trim() || '';
  }

  #resolveOptionValueName(element) {
    if (element?.dataset?.optionValueName) return element.dataset.optionValueName;
    if (element instanceof HTMLInputElement || element instanceof HTMLOptionElement) return element.value;
    return element.textContent?.trim() || '';
  }

  #resolveOptionPosition(element) {
    return element?.dataset?.fitOptionPosition || element?.dataset?.inputId?.split('-')?.[0] || element?.dataset?.optionValueId;
  }

  #syncLinkedOptionControls(sourceElement) {
    const optionValueId = sourceElement.dataset?.optionValueId;
    const optionPosition = this.#resolveOptionPosition(sourceElement);
    if (!optionValueId || !optionPosition) return;

    const select = this.querySelector(`select[data-fit-option-position="${optionPosition}"]`);
    if (select instanceof HTMLSelectElement) {
      const match = Array.from(select.options).find((option) => option.dataset.optionValueId === optionValueId);
      if (match) {
        select.value = match.value;
        for (const option of select.options) option.removeAttribute('selected');
        match.setAttribute('selected', 'selected');
      }
    }

    const proxyInputs = this.querySelectorAll(
      `input[data-fit-mobile-proxy="true"][data-fit-option-position="${optionPosition}"]`,
    );
    proxyInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.dataset.optionValueId === optionValueId;
      }
    });
  }
}

// Register custom element
if (!customElements.get('variant-select')) {
  customElements.define('variant-select', VariantPicker);
}
